import _ from 'lodash';
import createModel, { prop } from './lib/create_model';

import { NotificationSoundType } from 'actions/notification/lib/notification_constants';

// Mirrors the backend agentpreferences.ThemeMode enum. An empty/absent value is
// treated as light; the backend omits themeMode from the payload when unset, so
// a record predating the field never carries it (see the gateway observer).
export const ThemeMode = Object.freeze({
  LIGHT: 'LIGHT',
  DARK: 'DARK',
  AUTO: 'AUTO',
});

const AgentPreferences = createModel({
  modelName: 'AgentPreferences',

  properties: {
    // Preferences the agent explicitly sets.
    agentNotificationSound: prop(String).default(NotificationSoundType.AGENT_NOTIFICATION),
    answerSearchAudiences: prop([String]).default([]),
    answerSearchLanguage: prop(String).default(''),
    channelColors: prop(Object).default({}),
    channelNotificationSounds: prop(Object).default({}),
    conversationSummaryControlsEnabled: prop(Boolean).default(false),
    customizeChannelColors: prop(Boolean).default(false),
    customizeChannelNotificationSounds: prop(Boolean).default(false),
    detailedDatesEnabled: prop(Boolean).default(false),
    experimentalEditorEnabled: prop(Boolean).default(false),
    favoriteAnswerIds: prop([String]).default([]),
    pinnedNavItemIds: prop([String]).default([]),
    themeMode: prop(String).default(''),

    // Persisted state — not preferences, but data we want backed up across devices
    // (e.g. so the agent doesn't re-see a notification or feature tip elsewhere).
    agentNotificationsLastOpenedAt: prop(String),
    readFeatureNotifications: prop(Object).default({}),
    seenFeatureTips: prop(Object).default({}),
  },

  // Mirror the backend's `themeMode,omitempty`: don't send the empty default so an
  // agent who never picked a theme PUTs no themeMode at all rather than a non-enum
  // empty string on every unrelated prefs change.
  overrideToJs(originalToJs) {
    return function () {
      const json = originalToJs();
      if (json.themeMode === '') {
        delete json.themeMode;
      }
      return json;
    };
  },

  setAgentNotificationSound(sound) {
    this.agentNotificationSound = sound;
  },

  setAnswerSearchAudiences(audiences) {
    this.answerSearchAudiences = audiences;
  },

  setAnswerSearchLanguage(language) {
    this.answerSearchLanguage = language;
  },

  setChannelNotificationSound(channel, sound) {
    this.channelNotificationSounds = { ...this.channelNotificationSounds, [channel]: sound };
  },

  setCustomizeChannelNotificationSounds(enabled) {
    this.customizeChannelNotificationSounds = enabled;
  },

  setChannelColor(channel, color) {
    this.channelColors = { ...this.channelColors, [channel]: color };
  },

  setCustomizeChannelColors(enabled) {
    this.customizeChannelColors = enabled;
  },

  toggleConversationSummaryControls() {
    this.conversationSummaryControlsEnabled = !this.conversationSummaryControlsEnabled;
  },

  toggleDetailedDates() {
    this.detailedDatesEnabled = !this.detailedDatesEnabled;
  },

  setThemeMode(themeMode) {
    this.themeMode = themeMode;
  },

  toggleExperimentalEditor() {
    this.experimentalEditorEnabled = !this.experimentalEditorEnabled;
  },

  isAnswerFavorited(id) {
    return !!_.find(this.favoriteAnswerIds, favId => favId === id);
  },

  toggleFavoriteAnswer(id) {
    if (this.isAnswerFavorited(id)) {
      this.favoriteAnswerIds = _.filter(this.favoriteAnswerIds, favId => favId !== id);
    } else {
      this.favoriteAnswerIds.push(id);
    }
  },

  clearFavoriteAnswerIds() {
    this.favoriteAnswerIds = [];
  },

  isNavItemPinned(id) {
    return this.pinnedNavItemIds.includes(id);
  },

  toggleNavItemPin(id) {
    if (this.isNavItemPinned(id)) {
      this.pinnedNavItemIds = _.filter(this.pinnedNavItemIds, pinId => pinId !== id);
    } else {
      this.pinnedNavItemIds.push(id);
    }
  },

  setAgentNotificationsLastOpenedAt(lastOpenedAt) {
    this.agentNotificationsLastOpenedAt = lastOpenedAt;
  },

  hasSeenFeatureNotification(feature) {
    return !!this.readFeatureNotifications[feature];
  },

  markFeatureAsSeen(feature) {
    this.readFeatureNotifications[feature] = true;
  },

  hasSeenFeatureTip(feature) {
    return !!this.seenFeatureTips[feature];
  },

  markFeatureTipAsSeen(feature) {
    this.seenFeatureTips[feature] = true;
  },
});

export default AgentPreferences;
